import type { Airport, AirportSlotRequest, ServiceDay } from "../types";


export const SERVICE_DAYS: ServiceDay[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const SERVICE_DAY_LABELS: Record<ServiceDay, string> = {
  mon: "周一",
  tue: "周二",
  wed: "周三",
  thu: "周四",
  fri: "周五",
  sat: "周六",
  sun: "周日"
};

const JS_DAY_TO_SERVICE_DAY: ServiceDay[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function serviceDayForDate(dateIso: string): ServiceDay {
  return JS_DAY_TO_SERVICE_DAY[new Date(`${dateIso}T00:00:00Z`).getUTCDay()];
}

export function normalizeServiceDays(days: unknown): ServiceDay[] {
  let input: unknown[] = [];
  if (Array.isArray(days)) {
    input = days;
  }
  const unique = input.filter(isServiceDay).filter((day, index, list) => list.indexOf(day) === index);
  if (unique.length > 0) {
    return unique;
  }
  return [...SERVICE_DAYS];
}

export function buildFlightSlotRequests(origin: Airport, destination: Airport, day: ServiceDay, departureTime: string, blockMinutes: number): AirportSlotRequest[] {
  const normalizedDeparture = normalizeTime(departureTime);
  return [
    buildSlotRequest(`${origin.code}-${destination.code}-${day}-flight-dep`, origin, day, normalizedDeparture, "departure"),
    buildSlotRequest(`${origin.code}-${destination.code}-${day}-flight-arr`, destination, day, addMinutesToTime(normalizedDeparture, blockMinutes), "arrival")
  ];
}

export function addMinutesToTime(time: string, minutes: number): string {
  return minutesToTime(timeToMinutes(time) + Math.round(minutes));
}

export function normalizeTime(value: string): string {
  const match = /^(\d{1,2}):?(\d{2})?$/.exec(value.trim());
  if (!match) {
    return "08:30";
  }
  const hour = boundedTimePart(Number(match[1]), 0, 23);
  const minute = boundedTimePart(Number(match[2] ?? "00"), 0, 59);
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function timeToMinutes(value: string): number {
  const [hour, minute] = normalizeTime(value)
    .split(":")
    .map((part) => Number(part));
  return hour * 60 + minute;
}

export function minutesToTime(minutes: number): string {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function dayMinuteToLabel(minutes: number): string {
  const dayOffset = Math.floor(minutes / 1440);
  const suffix = dayOffsetLabel(dayOffset);
  return `${minutesToTime(minutes)}${suffix}`;
}

export function slotDelayMinutes(slots: AirportSlotRequest[]): number {
  return slots.reduce((sum, slot) => sum + slotDelayForStatus(slot.status), 0);
}

export function slotBlockNotes(slots: AirportSlotRequest[]): string[] {
  return slots
    .filter((slot) => slot.status === "waitlist")
    .map((slot) => `${slot.airportCode} ${SERVICE_DAY_LABELS[slot.day]} ${slot.timeLocal} ${slotKindLabel(slot.kind)}时刻未协调。`);
}

function buildSlotRequest(id: string, airport: Airport, day: ServiceDay, timeLocal: string, kind: AirportSlotRequest["kind"]): AirportSlotRequest {
  const capacityScore = calculateSlotCapacityScore(airport, timeLocal, kind);
  return {
    id,
    airportCode: airport.code,
    day,
    timeLocal,
    kind,
    status: slotRequestStatus(capacityScore),
    capacityScore
  };
}

function calculateSlotCapacityScore(airport: Airport, timeLocal: string, kind: AirportSlotRequest["kind"]): number {
  const hour = Number(timeLocal.slice(0, 2));
  const peakPenalty = peakPenaltyForHour(hour);
  const arrivalPenalty = arrivalSlotPenalty(kind);
  const rawCapacityScore = airport.slotCapacity - peakPenalty - arrivalPenalty;
  const cappedCapacityScore = Math.min(98, rawCapacityScore);
  return Math.max(12, cappedCapacityScore);
}

function boundedTimePart(value: number, min: number, max: number): number {
  const cappedValue = Math.min(max, value);
  return Math.max(min, cappedValue);
}

function dayOffsetLabel(dayOffset: number): string {
  if (dayOffset > 0) {
    return ` +${dayOffset}`;
  }
  return "";
}

function slotDelayForStatus(status: AirportSlotRequest["status"]): number {
  if (status === "requested") {
    return 18;
  }
  return 0;
}

function slotKindLabel(kind: AirportSlotRequest["kind"]): string {
  if (kind === "departure") {
    return "离港";
  }
  return "进港";
}

function arrivalSlotPenalty(kind: AirportSlotRequest["kind"]): number {
  if (kind === "arrival") {
    return 3;
  }
  return 0;
}

function slotRequestStatus(capacityScore: number): AirportSlotRequest["status"] {
  if (capacityScore >= 58) {
    return "coordinated";
  }
  if (capacityScore >= 42) {
    return "requested";
  }
  return "waitlist";
}

function peakPenaltyForHour(hour: number): number {
  if (hour >= 7 && hour <= 9) {
    return 22;
  }
  if (hour >= 17 && hour <= 20) {
    return 18;
  }
  if (hour <= 5) {
    return 4;
  }
  return 0;
}

function isServiceDay(value: unknown): value is ServiceDay {
  return typeof value === "string" && SERVICE_DAYS.includes(value as ServiceDay);
}
